import { ValueOf } from "ts-essentials";

export const LOCAL_CLIENT_PORT = 3000;
export const LOCAL_API_PORT = 8000;

type PathParameterSelectors = Omit<
	typeof PATH_PARAMETER_SELECTORS,
	"prototype"
>;
export type PathParameterSelector = ValueOf<PathParameterSelectors>;

export const getBasePathForHostname = (): string => {
	if (typeof window !== 'undefined') {
		const hostname = window.location.hostname;
		if (hostname.includes('developer')) {
			console.log("=== routes.ts | Using /partnerships/analytics prefix on devex host ===");
			return "/partnerships/analytics";
		}
		console.log("=== routes.ts | Using empty prefix on legacy host ===");
		return "";
	}
	// Default for SSR or build time
	return "";
};

let PARTNER_PORTAL_PREFIX = getBasePathForHostname();

export class PATH_PARAMETER_SELECTORS {
	static readonly ACCESS_TYPE_CODE = ":accessTypeCode";
	static readonly PARTNER_CODE = ":partnerCode";
	static readonly REPORT_CATEGORY_CODE = ":reportCategoryCode";
	static readonly REPORT_ID = ":reportId";
	static readonly FREQUENCY_TYPE_CODE = ":frequencyTypeCode";
	static readonly USER_ID = ":userId";
	static readonly INVITATION_ID = ":invitationId";
	static readonly FREQUENTLY_ASKED_QUESTION_ID = ":frequentlyAskedQuestionId";
	static readonly RECAPTCHA_WIDGET_RESPONSE = ":reCaptchaWidgetResponse";
	static readonly FORM_RECORD_ID = ":recordId";
	static readonly DASHBOARD_ID = ":dashboardId";
	static readonly EMAIL = ":email";
	static readonly TENANT_ID = ":tenantId";
	static readonly PINBOARD_ID = ":pinboardId";
	static readonly GUID = ":guid";
	static readonly BANNER_ID = ":bannerId";
	static readonly REPORT_MONITOR_ID = ":reportMonitorId";
	static readonly CATALOG_ID = ":catalogId";
}

export class API_PATHS {
	static readonly LOGIN = PARTNER_PORTAL_PREFIX + "/api/oauth2/authorization/copp";
	static readonly LOGIN_REDIRECT = PARTNER_PORTAL_PREFIX + "/api/login/redirect";

	static readonly GET_ENVIRONMENT_CONTEXT = PARTNER_PORTAL_PREFIX + "/api/environment-context";
	static readonly GET_USER = PARTNER_PORTAL_PREFIX + "/api/user";
	static readonly GET_IS_USER_AUTHENTICATED = PARTNER_PORTAL_PREFIX + "/api/user/authenticated";

	static readonly GET_ALL_PARTNERS = PARTNER_PORTAL_PREFIX + "/api/partners";
	static readonly GET_ALL_MARKETING_CONTENT_PARTNERS = PARTNER_PORTAL_PREFIX +
		"/api/marketing/partners";
	static readonly GET_REPORT_CATEGORIES_FOR_PARTNER = PARTNER_PORTAL_PREFIX +
		"/api/categories/:accessTypeCode/:partnerCode";
	static readonly GET_REPORTS = PARTNER_PORTAL_PREFIX +
		"/api/reports/:accessTypeCode/:partnerCode/:reportCategoryCode";
	static readonly VIEW_REPORT = PARTNER_PORTAL_PREFIX + "/api/reports/view/:reportId";
	static readonly DOWNLOAD_REPORTS = PARTNER_PORTAL_PREFIX + "/api/reports/download";

	static readonly GET_ALL_PARTNERS_ADMIN = PARTNER_PORTAL_PREFIX + "/api/admin/partners";
	static readonly GET_PARTNER_ADMIN = PARTNER_PORTAL_PREFIX + "/api/admin/partners/:partnerCode";
	static readonly GET_ALL_REPORT_CATEGORIES_ADMIN = PARTNER_PORTAL_PREFIX + "/api/admin/categories";
	static readonly GET_ALL_FREQUENCY_TYPES_ADMIN = PARTNER_PORTAL_PREFIX + "/api/frequency-types";
	static readonly GET_ALL_USERS_ADMIN = PARTNER_PORTAL_PREFIX + "/api/users";
	static readonly GET_SAAS_USERS = PARTNER_PORTAL_PREFIX + "/api/saas/user/:tenantId";
	static readonly GET_ALL_SAAS_USERS = PARTNER_PORTAL_PREFIX + "/api/saas/user/:tenantId/all";
	static readonly GET_ALL_SAAS_USER_ROLES = PARTNER_PORTAL_PREFIX + "/api/saas/role";
	static readonly GET_ALL_SAAS_USER_INVITATIONS = PARTNER_PORTAL_PREFIX +
		"/api/saas/user/:tenantId/invitations";
	static readonly UPDATE_CURRENT_SAAS_USER = PARTNER_PORTAL_PREFIX + "/api/saas/user/updateSelf";
	static readonly GET_EXTERNAL_USERS_ADMIN = PARTNER_PORTAL_PREFIX + "/api/external-users";
	static readonly GET_ALL_FREQUENTLY_ASKED_QUESTIONS_ADMIN = PARTNER_PORTAL_PREFIX + "/api/faqs";
	static readonly GET_TENANTS = PARTNER_PORTAL_PREFIX + "/api/saas/tenant";
	static readonly CREATE_PARTNER = PARTNER_PORTAL_PREFIX + "/api/partners";
	static readonly CREATE_REPORT_CATEGORY = PARTNER_PORTAL_PREFIX + "/api/categories";
	static readonly CREATE_FREQUENCY_TYPE = PARTNER_PORTAL_PREFIX + "/api/frequency-types";
	static readonly CREATE_USER = PARTNER_PORTAL_PREFIX + "/api/users";
	static readonly INVITE_SAAS_USER = PARTNER_PORTAL_PREFIX + "/api/saas/user/tenantId/:tenantId/invite";
	static readonly EDIT_SAAS_USER_ROLES = PARTNER_PORTAL_PREFIX + "/api/saas/user/:tenantId/:userId/role/:email";
	static readonly CREATE_FREQUENTLY_ASKED_QUESTION = PARTNER_PORTAL_PREFIX + "/api/faqs";
	static readonly EDIT_PARTNER = PARTNER_PORTAL_PREFIX + "/api/partners";
	static readonly EDIT_REPORT_CATEGORY = PARTNER_PORTAL_PREFIX + "/api/categories";
	static readonly EDIT_FREQUENCY_TYPE = PARTNER_PORTAL_PREFIX + "/api/frequency-types";
	static readonly EDIT_USER = PARTNER_PORTAL_PREFIX + "/api/users";
	static readonly EDIT_FREQUENTLY_ASKED_QUESTION = PARTNER_PORTAL_PREFIX + "/api/faqs";
	static readonly DELETE_PARTNER = PARTNER_PORTAL_PREFIX + "/api/partners/:accessTypeCode/:partnerCode";
	static readonly DELETE_REPORT_CATEGORY = PARTNER_PORTAL_PREFIX +
		"/api/categories/:reportCategoryCode";
	static readonly DELETE_REPORT = PARTNER_PORTAL_PREFIX + "/api/reports/:reportId";
	static readonly DELETE_FREQUENCY_TYPE = PARTNER_PORTAL_PREFIX +
		"/api/frequency-types/:frequencyTypeCode";
	static readonly DELETE_SAAS_USER = PARTNER_PORTAL_PREFIX +
		"/api/saas/user/:tenantId/:userId/email/:email";
	static readonly DELETE_SAAS_USER_INVITATION = PARTNER_PORTAL_PREFIX +
		"/api/saas/user/:tenantId/invitations/:invitationId/email/:email";
	static readonly DELETE_USER = PARTNER_PORTAL_PREFIX + "/api/users/:userId";
	static readonly DELETE_FREQUENTLY_ASKED_QUESTION = PARTNER_PORTAL_PREFIX +
		"/api/faqs/:frequentlyAskedQuestionId";

	static readonly GET_FREQUENTLY_ASKED_QUESTIONS = PARTNER_PORTAL_PREFIX + "/api/faqs";
	static readonly GET_REPORTABLE_ISSUES = PARTNER_PORTAL_PREFIX + "/api/reasons";
	static readonly SUBMIT_REPORT_AN_ISSUE_FORM = PARTNER_PORTAL_PREFIX + "/api/contact-us";
	static readonly LOGOUT = PARTNER_PORTAL_PREFIX + "/api/logout";

	static readonly VALIDATE_RECAPTCHA = PARTNER_PORTAL_PREFIX + "/api/validate-recaptcha";

	static readonly GET_ALL_CUSTOMER_INVOICES = PARTNER_PORTAL_PREFIX + "/api/form/user-invoices";
	static readonly GET_ALL_WALMART_DISPUTES = PARTNER_PORTAL_PREFIX + "/api/form/disputes";
	static readonly GET_HISTORIC_WALMART_DISPUTES = PARTNER_PORTAL_PREFIX + "/api/form/disputes/history";
	static readonly CREATE_CUSTOMER_INVOICE = PARTNER_PORTAL_PREFIX + "/api/form/user-invoices";
	static readonly CREATE_WALMART_DISPUTES = PARTNER_PORTAL_PREFIX + "/api/form/disputes";
	static readonly EDIT_CUSTOMER_INVOICE = PARTNER_PORTAL_PREFIX + "/api/form/user-invoices";
	static readonly EDIT_WALMART_DISPUTES = PARTNER_PORTAL_PREFIX + "/api/form/disputes";
	static readonly DELETE_CUSTOMER_INVOICE = PARTNER_PORTAL_PREFIX + "/api/form/user-invoices/:recordId";
	static readonly DELETE_WALMART_DISPUTES = PARTNER_PORTAL_PREFIX + "/api/form/disputes/:recordId";

	static readonly GET_DASHBOARD_BY_ID = PARTNER_PORTAL_PREFIX +
		"/api/dashboards/:accessTypeCode/:partnerCode/:dashboardId";
	static readonly GET_ALL_DASHBOARD_PARTNERS = PARTNER_PORTAL_PREFIX + "/api/dashboards/partners";
	static readonly GET_DASHBOARDS_FOR_PARTNER = PARTNER_PORTAL_PREFIX +
		"/api/dashboards/:accessTypeCode/:partnerCode";
	static readonly GET_ALL_DASHBOARDS_ADMIN = PARTNER_PORTAL_PREFIX + "/api/dashboards";
	static readonly CREATE_DASHBOARD = PARTNER_PORTAL_PREFIX + "/api/dashboards";
	static readonly EDIT_DASHBOARD = PARTNER_PORTAL_PREFIX + "/api/dashboards/:dashboardId";
	static readonly DELETE_DASHBOARD = PARTNER_PORTAL_PREFIX + "/api/dashboards/:dashboardId";
	static readonly GET_DASHBOARD_CONTENT_TYPES = PARTNER_PORTAL_PREFIX +
		"/api/dashboards/content-types/:accessTypeCode/:partnerCode";

	static readonly TRACK_DASHBOARD_VIEW = PARTNER_PORTAL_PREFIX +
		"/api/activity/track/dashboard-view/:dashboardId";
	static readonly TRACK_DASHBOARD_FAIL = PARTNER_PORTAL_PREFIX +
		"/api/activity/track/dashboard-fail/:dashboardId";

	static readonly GET_QUICKSIGHT_BOARD_INFO = PARTNER_PORTAL_PREFIX + "/api/quicksight/:dashboardId";
	static readonly GET_LATEST_MARKETING_IMAGE = PARTNER_PORTAL_PREFIX +
		"/api/marketing/latest/:partnerCode";
	static readonly UPLOAD_MARKETING_IMAGE = PARTNER_PORTAL_PREFIX + "/api/marketing/upload/:partnerCode";
	static readonly GET_LATEST_PARTNER_LOGO = PARTNER_PORTAL_PREFIX + "/api/logo/:partnerCode";
	static readonly UPLOAD_PARTNER_LOGO = PARTNER_PORTAL_PREFIX + "/api/logo/:partnerCode";
	static readonly GET_CURRENT_BANNER = PARTNER_PORTAL_PREFIX + "/api/banner";
	static readonly CREATE_BANNER = PARTNER_PORTAL_PREFIX + "/api/banner";
	static readonly EDIT_BANNER = PARTNER_PORTAL_PREFIX + "/api/banner/:bannerId/edit";
	static readonly DISABLE_BANNER = PARTNER_PORTAL_PREFIX + "/api/banner/:bannerId/disable";

	static readonly GET_ALL_SCHEDULED_REPORT_MONITORS = PARTNER_PORTAL_PREFIX + "/api/report-monitors";
	static readonly CREATE_SCHEDULED_REPORT_MONITOR = PARTNER_PORTAL_PREFIX + "/api/report-monitors";
	static readonly UPDATE_SCHEDULED_REPORT_MONITOR = PARTNER_PORTAL_PREFIX +
		"/api/report-monitors/:reportMonitorId";
	static readonly DELETE_SCHEDULED_REPORT_MONITOR = PARTNER_PORTAL_PREFIX +
		"/api/report-monitors/:reportMonitorId";

	static readonly GET_DATASET = PARTNER_PORTAL_PREFIX + "/api/datasets/:catalogId";

	static readonly GET_MOST_VIEWED_DASHBOARDS_MONTH = PARTNER_PORTAL_PREFIX +
		"/api/snowflake/activity-logs/popular-dashboards/:accessTypeCode/:partnerCode";
}

type ClientPaths = Omit<typeof CLIENT_PATHS, "prototype">;
export type ClientPath = ValueOf<ClientPaths>;

export class CLIENT_PATHS {
	static readonly HOME_PAGE =  "/";
	static readonly HOME_PAGE_V2 = "/partnerships/analytics";
	static readonly SELECT_PARTNER_PAGE = PARTNER_PORTAL_PREFIX + "/partners";
	static readonly SELECT_TENANT_PAGE = PARTNER_PORTAL_PREFIX + "/tenants";
	static readonly SELECT_REPORT_CATEGORY_PAGE = PARTNER_PORTAL_PREFIX +
		"/partners/:accessTypeCode/:partnerCode/report-categories";
	static readonly DOWNLOAD_REPORTS_PAGE = PARTNER_PORTAL_PREFIX +
		"/partners/:accessTypeCode/:partnerCode/report-categories/:reportCategoryCode/reports";
	static readonly MARKETPLACE_PAGE = PARTNER_PORTAL_PREFIX + "/marketplace";
	static readonly MARKETING_SELECT_PARTNER_PAGE = PARTNER_PORTAL_PREFIX + "/marketing";
	static readonly MANAGE_MARKETING_CONTENT_PAGE = PARTNER_PORTAL_PREFIX +
		"/marketing/latest/:partnerCode";
	static readonly SELECT_OPERATIONAL_FORM_PAGE = PARTNER_PORTAL_PREFIX + "/forms";
	static readonly CUSTOMER_INVOICE_FORM_PAGE = PARTNER_PORTAL_PREFIX + "/forms/customer-invoice";
	static readonly CREATE_CUSTOMER_INVOICE_PAGE = PARTNER_PORTAL_PREFIX + "/forms/customer-invoice/new";
	static readonly EDIT_CUSTOMER_INVOICE_PAGE = PARTNER_PORTAL_PREFIX +
		"/forms/customer-invoice/:recordId/edit";
	static readonly WALMART_DISPUTE_FORM_PAGE = PARTNER_PORTAL_PREFIX + "/forms/walmart-disputes";
	static readonly CREATE_WALMART_DISPUTE_PAGE = PARTNER_PORTAL_PREFIX + "/forms/walmart-disputes/new";
	static readonly EDIT_WALMART_DISPUTE_PAGE = PARTNER_PORTAL_PREFIX +
		"/forms/walmart-disputes/:recordId/edit";

	static readonly ADMIN_PAGE = PARTNER_PORTAL_PREFIX + "/admin";
	static readonly MANAGE_PARTNERS_PAGE = PARTNER_PORTAL_PREFIX + "/admin/partners";
	static readonly MANAGE_REPORT_ALERTS_PAGE = PARTNER_PORTAL_PREFIX + "/admin/report-alerts";
	static readonly MANAGE_REPORT_CATEGORIES_PAGE = PARTNER_PORTAL_PREFIX + "/admin/report-categories";
	static readonly MANAGE_FREQUENCY_TYPES_PAGE = PARTNER_PORTAL_PREFIX + "/admin/frequency-types";
	static readonly MANAGE_EXTERNAL_USERS_PAGE = PARTNER_PORTAL_PREFIX + "/admin/users";
	static readonly MANAGE_SAAS_USERS_PAGE = PARTNER_PORTAL_PREFIX + "/admin/:tenantId/saas-users";
	static readonly MANAGE_SAAS_USER_INVITATIONS_PAGE = PARTNER_PORTAL_PREFIX +
		"/admin/:tenantId/saas-users/invitations";
	static readonly MANAGE_FREQUENTLY_ASKED_QUESTIONS_PAGE = PARTNER_PORTAL_PREFIX + "/admin/faqs";
	static readonly CREATE_PARTNER_PAGE = PARTNER_PORTAL_PREFIX + "/admin/partners/new";
	static readonly CREATE_REPORT_CATEGORY_PAGE = PARTNER_PORTAL_PREFIX + "/admin/report-categories/new";
	static readonly CREATE_FREQUENCY_TYPE_PAGE = PARTNER_PORTAL_PREFIX + "/admin/frequency-types/new";
	static readonly CREATE_FREQUENTLY_ASKED_QUESTION_PAGE = PARTNER_PORTAL_PREFIX + "/admin/faqs/new";
	static readonly CREATE_USER_PAGE = PARTNER_PORTAL_PREFIX + "/admin/users/new";
	static readonly INVITE_SAAS_USER_PAGE = PARTNER_PORTAL_PREFIX + "/admin/:tenantId/saas-users/new";
	static readonly EDIT_PARTNER_PAGE = PARTNER_PORTAL_PREFIX +
		"/admin/partners/:accessTypeCode/:partnerCode/edit";
	static readonly EDIT_REPORT_CATEGORY_PAGE = PARTNER_PORTAL_PREFIX +
		"/admin/report-categories/:reportCategoryCode/edit";
	static readonly EDIT_FREQUENCY_TYPE_PAGE = PARTNER_PORTAL_PREFIX +
		"/admin/frequency-types/:frequencyTypeCode/edit";
	static readonly EDIT_USER_PAGE = PARTNER_PORTAL_PREFIX + "/admin/users/:userId/edit";
	static readonly EDIT_SAAS_USER_PAGE = PARTNER_PORTAL_PREFIX +
		"/admin/:tenantId/saas-users/edit/:userId";
	static readonly EDIT_FREQUENTLY_ASKED_QUESTION_PAGE = PARTNER_PORTAL_PREFIX +
		"/admin/faqs/:frequentlyAskedQuestionId/edit";
	static readonly MANAGE_DASHBOARDS_PAGE = PARTNER_PORTAL_PREFIX + "/admin/dashboards";
	static readonly MANAGE_BANNER_PAGE = PARTNER_PORTAL_PREFIX + "/admin/banner";

	static readonly DASHBOARDS_SELECT_PARTNER_PAGE = PARTNER_PORTAL_PREFIX + "/dashboards/partners";
	static readonly DASHBOARDS_SELECT_PAGE = PARTNER_PORTAL_PREFIX +
		"/dashboards/:accessTypeCode/:partnerCode";
	static readonly RSTUDIO_LIVEBOARD_PAGE = PARTNER_PORTAL_PREFIX +
		"/dashboards/rstudio/:accessTypeCode/:partnerCode/:dashboardId";
	static readonly QUICKSIGHT_PAGE = PARTNER_PORTAL_PREFIX +
		"/dashboards/quicksight/:accessTypeCode/:partnerCode/:pinboardId";
	static readonly QUICKSIGHT_PAGE_V2 =
		"/partnerships/analytics/dashboard/:dashboardId";
	static readonly TABLEAU_PAGE = PARTNER_PORTAL_PREFIX +
		"/dashboards/tableau/:accessTypeCode/:partnerCode/:dashboardId";

	static readonly FREQUENTLY_ASKED_QUESTIONS_PAGE = PARTNER_PORTAL_PREFIX + "/faqs";
	static readonly MY_ACCOUNT_PAGE =  PARTNER_PORTAL_PREFIX +"/my-account";
	static readonly REPORT_AN_ISSUE_PAGE = PARTNER_PORTAL_PREFIX + "/report-an-issue";
	static readonly LOGOUT = PARTNER_PORTAL_PREFIX + "/logout";
	static readonly LOGIN = PARTNER_PORTAL_PREFIX + "/login";

	static readonly CAPITAL_ONE_HOME_PAGE = "https://www.capitalone.com/";
}

export type PageName = string;

export const PATH_TO_PAGE_NAME_MAP: Record<string, string> = {
	[CLIENT_PATHS.HOME_PAGE]: "Capital One Partner Portal",
	[CLIENT_PATHS.HOME_PAGE_V2]: "Analytics",
	[CLIENT_PATHS.SELECT_PARTNER_PAGE]: "Partners",
	[CLIENT_PATHS.SELECT_TENANT_PAGE]: "Manage Users",
	[CLIENT_PATHS.SELECT_REPORT_CATEGORY_PAGE]: "Report Categories",
	[CLIENT_PATHS.DOWNLOAD_REPORTS_PAGE]: "Reports",
	[CLIENT_PATHS.MARKETPLACE_PAGE]: "Marketplace",
	[CLIENT_PATHS.MARKETING_SELECT_PARTNER_PAGE]: "Manage Marketing Content",
	[CLIENT_PATHS.MANAGE_MARKETING_CONTENT_PAGE]: "Upload Content",
	[CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE]: "Operational Forms",
	[CLIENT_PATHS.CUSTOMER_INVOICE_FORM_PAGE]: "Customer Invoice Form",
	[CLIENT_PATHS.CREATE_CUSTOMER_INVOICE_PAGE]: "Add Customer Invoice",
	[CLIENT_PATHS.EDIT_CUSTOMER_INVOICE_PAGE]: "Edit Customer Invoice",
	[CLIENT_PATHS.WALMART_DISPUTE_FORM_PAGE]: "Walmart Commercial Disputes Form",
	[CLIENT_PATHS.CREATE_WALMART_DISPUTE_PAGE]: "Add Walmart Commercial Dispute",
	[CLIENT_PATHS.EDIT_WALMART_DISPUTE_PAGE]: "Edit Walmart Commercial Dispute",
	[CLIENT_PATHS.ADMIN_PAGE]: "Admin",
	[CLIENT_PATHS.MANAGE_PARTNERS_PAGE]: "Manage Partners",
	[CLIENT_PATHS.MANAGE_REPORT_ALERTS_PAGE]: "Manage Report Alerts",
	[CLIENT_PATHS.MANAGE_REPORT_CATEGORIES_PAGE]: "Manage Report Categories",
	[CLIENT_PATHS.MANAGE_FREQUENCY_TYPES_PAGE]: "Manage Frequency Types",
	[CLIENT_PATHS.MANAGE_EXTERNAL_USERS_PAGE]: "Manage Internal Users",
	[CLIENT_PATHS.MANAGE_SAAS_USERS_PAGE]: "Users",
	[CLIENT_PATHS.MANAGE_SAAS_USER_INVITATIONS_PAGE]: "Manage Auth0 User Invitations",
	[CLIENT_PATHS.MANAGE_FREQUENTLY_ASKED_QUESTIONS_PAGE]: "Manage Frequently Asked Questions",
	[CLIENT_PATHS.CREATE_PARTNER_PAGE]: "Add Partner",
	[CLIENT_PATHS.CREATE_REPORT_CATEGORY_PAGE]: "Add Report Category",
	[CLIENT_PATHS.CREATE_FREQUENCY_TYPE_PAGE]: "Add Frequency Type",
	[CLIENT_PATHS.CREATE_USER_PAGE]: "Add External User",
	[CLIENT_PATHS.INVITE_SAAS_USER_PAGE]: "Invite a New User",
	[CLIENT_PATHS.CREATE_FREQUENTLY_ASKED_QUESTION_PAGE]: "Add Frequently Asked Question",
	[CLIENT_PATHS.EDIT_PARTNER_PAGE]: "Edit Partner",
	[CLIENT_PATHS.EDIT_REPORT_CATEGORY_PAGE]: "Edit Report Category",
	[CLIENT_PATHS.EDIT_FREQUENCY_TYPE_PAGE]: "Edit Frequency Type",
	[CLIENT_PATHS.EDIT_USER_PAGE]: "Edit External User",
	[CLIENT_PATHS.EDIT_SAAS_USER_PAGE]: "Update User Role",
	[CLIENT_PATHS.EDIT_FREQUENTLY_ASKED_QUESTION_PAGE]: "Edit Frequently Asked Question",
	[CLIENT_PATHS.MANAGE_DASHBOARDS_PAGE]: "Manage Dashboards",
	[CLIENT_PATHS.MANAGE_BANNER_PAGE]: "Manage Banner",
	[CLIENT_PATHS.DASHBOARDS_SELECT_PARTNER_PAGE]: "Partners",
	[CLIENT_PATHS.DASHBOARDS_SELECT_PAGE]: "Dashboards",
	[CLIENT_PATHS.RSTUDIO_LIVEBOARD_PAGE]: "RStudioLiveboard",
	[CLIENT_PATHS.QUICKSIGHT_PAGE_V2]: "Analytics Dashboard",
	[CLIENT_PATHS.TABLEAU_PAGE]: "Tableau",
	[CLIENT_PATHS.FREQUENTLY_ASKED_QUESTIONS_PAGE]: "Frequently Asked Questions",
	[CLIENT_PATHS.REPORT_AN_ISSUE_PAGE]: "Report an Issue",
	[CLIENT_PATHS.LOGOUT]: "You have been logged out.",
	[CLIENT_PATHS.LOGIN]: "",
	[CLIENT_PATHS.CAPITAL_ONE_HOME_PAGE]: "Capital One",
	[CLIENT_PATHS.MY_ACCOUNT_PAGE]: "My Account",
};

export const PATH_TO_MENU_ITEM_NAME_MAP: Record<string, string> = Object.freeze(
	{
		...Object.fromEntries(Object.entries(PATH_TO_PAGE_NAME_MAP)),

		[CLIENT_PATHS.HOME_PAGE]: "Home",
		[CLIENT_PATHS.HOME_PAGE_V2]: "Analytics",

		[CLIENT_PATHS.SELECT_PARTNER_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.DOWNLOAD_REPORTS_PAGE],
		[CLIENT_PATHS.SELECT_REPORT_CATEGORY_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.DOWNLOAD_REPORTS_PAGE],

		[CLIENT_PATHS.CREATE_PARTNER_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.MANAGE_PARTNERS_PAGE],
		[CLIENT_PATHS.EDIT_PARTNER_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.MANAGE_PARTNERS_PAGE],

		[CLIENT_PATHS.CREATE_REPORT_CATEGORY_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.MANAGE_REPORT_CATEGORIES_PAGE],
		[CLIENT_PATHS.EDIT_REPORT_CATEGORY_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.MANAGE_REPORT_CATEGORIES_PAGE],

		[CLIENT_PATHS.CREATE_FREQUENCY_TYPE_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.MANAGE_FREQUENCY_TYPES_PAGE],
		[CLIENT_PATHS.EDIT_FREQUENCY_TYPE_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.MANAGE_FREQUENCY_TYPES_PAGE],

		[CLIENT_PATHS.CREATE_USER_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.MANAGE_EXTERNAL_USERS_PAGE],
		[CLIENT_PATHS.EDIT_USER_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.MANAGE_EXTERNAL_USERS_PAGE],

		[CLIENT_PATHS.MANAGE_FREQUENTLY_ASKED_QUESTIONS_PAGE]: "Manage FAQs",
		[CLIENT_PATHS.CREATE_FREQUENTLY_ASKED_QUESTION_PAGE]: "Manage FAQs",
		[CLIENT_PATHS.EDIT_FREQUENTLY_ASKED_QUESTION_PAGE]: "Manage FAQs",

		[CLIENT_PATHS.CUSTOMER_INVOICE_FORM_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE],
		[CLIENT_PATHS.CREATE_CUSTOMER_INVOICE_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE],
		[CLIENT_PATHS.EDIT_CUSTOMER_INVOICE_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE],

		[CLIENT_PATHS.WALMART_DISPUTE_FORM_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE],
		[CLIENT_PATHS.CREATE_WALMART_DISPUTE_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE],
		[CLIENT_PATHS.EDIT_WALMART_DISPUTE_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE],

		[CLIENT_PATHS.DASHBOARDS_SELECT_PARTNER_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.DASHBOARDS_SELECT_PAGE],
		[CLIENT_PATHS.DASHBOARDS_SELECT_PAGE]:
			PATH_TO_PAGE_NAME_MAP[CLIENT_PATHS.RSTUDIO_LIVEBOARD_PAGE],

		[CLIENT_PATHS.LOGOUT]: "Log Out"
	}
);
